// ── 월별 달력 뷰 ──────────────────────────────────────────────────────────────
import React, { useMemo } from 'react';
import { STATUS_META, WEEKDAY_NAMES, WEEKDAY_ORDER, getTodayYmd, buildMonthGrid, timeToPercent } from './utils';

/**
 * 월별 달력 그리드 컴포넌트
 * Props:
 *   calData        — API 응답 달력 데이터
 *   year, month    — 표시 연/월
 *   filteredCells  — 표시할 셀 키 Set ('YYYY-MM-DD:userId')
 *   onDayClick(day) — 날짜 클릭 콜백. 칸 안 어디를 눌러도(이름 칩 위 포함) 날짜 상세 창이 뜬다.
 *   (2026-10-01 사용자: 이름 칩 위를 누르면 직원별 상세로 바로 넘어가 날짜 상세를 보려면 빈 곳을 찾아 눌러야 했다 →
 *    칩의 "직원별 상세로" 를 빼고, 날짜 상세 창 안의 이름에서 넘어가게 옮겼다 — DayDetailModal onPickPerson)
 */
export function MonthView({ calData, year, month, filteredCells, onDayClick }) {
  const grid     = useMemo(() => buildMonthGrid(year, month), [year, month]);
  const todayYmd = getTodayYmd();

  const dayMap = useMemo(() => {
    const map = {};
    (calData?.days || []).forEach((d) => { map[d.date] = d; });
    return map;
  }, [calData]);

  return (
    <div style={{ overflowX: 'auto' }}>
      {/* 요일 헤더 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 2 }}>
        {WEEKDAY_ORDER.map((wd) => (
          <div key={wd} style={{
            textAlign: 'center', fontSize: 12, fontWeight: 700,
            padding: '6px 0',
            color: wd === 'sat' ? '#2563eb' : wd === 'sun' ? '#dc2626' : 'var(--text-sub)',
            background: 'var(--bg-subtle)', borderRadius: 4,
          }}>
            {WEEKDAY_NAMES[wd]}
          </div>
        ))}
      </div>

      {/* 달력 그리드 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {grid.map((ymd, idx) => {
          if (!ymd) return <div key={`empty-${idx}`} style={{ minHeight: 100, background: 'var(--bg-subtle)', borderRadius: 6 }} />;
          const day = dayMap[ymd];
          if (!day) return (
            <div key={ymd} style={{ minHeight: 100, background: 'var(--bg-subtle)', borderRadius: 6, padding: 4 }}>
              <span style={{ fontSize: 13, color: 'var(--border)' }}>{ymd.slice(8)}</span>
            </div>
          );

          const isToday = ymd === todayYmd;
          const wd      = day.weekday;
          const dayNum  = Number(ymd.slice(8));
          const visibleCells = day.cells.filter((c) => filteredCells.has(`${ymd}:${c.user_id}`));
          // A안 (2026-09-01 사용자 확정): 미래 날짜의 '예정' 칩은 개별로 그리지 않고
          //   옅은 한 줄("· 근무 예정 N명")로 축약 — 연차·반차·휴무·공휴일 등 특이 사유만 칩 유지.
          //   날짜 클릭 상세(예정 근무지 설정 등)는 그대로 동작.
          const isFuture       = ymd > todayYmd;
          const scheduledCount = isFuture ? visibleCells.filter((c) => c.status === 'scheduled').length : 0;
          const chipCells      = isFuture ? visibleCells.filter((c) => c.status !== 'scheduled') : visibleCells;

          return (
            <div
              key={ymd}
              onClick={() => onDayClick(day)}
              style={{
                minHeight: 100, background: 'var(--bg-card)', borderRadius: 6,
                border: isToday ? '2px solid #3b82f6' : '1px solid var(--border)',
                padding: '4px 5px', cursor: 'pointer', transition: 'box-shadow 0.1s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
            >
              {/* 날짜 번호 + 요약 뱃지 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                <span style={{
                  fontSize: 13, fontWeight: isToday ? 700 : 600,
                  color: day.is_holiday || wd === 'sun' ? '#dc2626' : wd === 'sat' ? '#2563eb' : isToday ? '#3b82f6' : 'var(--text-sub)',
                }}>
                  {dayNum}
                  {/* 공휴일명 표시 */}
                  {day.is_holiday && <span style={{ fontSize: 9, marginLeft: 3, fontWeight: 600 }}>{day.holiday_name || '공휴일'}</span>}
                </span>
                <div style={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {day.summary.on_leave > 0 && <span style={{ fontSize: 10, background: '#dbeafe', color: '#1e40af', borderRadius: 3, padding: '1px 4px' }}>연차{day.summary.on_leave}</span>}
                  {day.summary.absent > 0 && <span style={{ fontSize: 10, background: '#fee2e2', color: '#991b1b', borderRadius: 3, padding: '1px 4px' }}>결근{day.summary.absent}</span>}
                  {day.summary.present > 0 && <span style={{ fontSize: 10, background: '#dcfce7', color: '#166534', borderRadius: 3, padding: '1px 4px' }}>출근{day.summary.present}</span>}
                  {day.leave_overlap_warning && <span style={{ fontSize: 10, background: '#fef3c7', color: '#92400e', borderRadius: 3, padding: '1px 4px' }}>⚠️</span>}
                </div>
              </div>

              {/* 직원 칩 (최대 4명, 초과 시 +N 표시) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {scheduledCount > 0 && (
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', padding: '1px 2px' }}>
                    · 근무 예정 {scheduledCount}명
                  </div>
                )}
                {chipCells.slice(0, 4).map((cell) => {
                  // 오늘이면서 예정인 경우 → "미출근" 처리
                  const isTodayNotIn = ymd === todayYmd && cell.status === 'scheduled' && cell.is_workday;
                  const isLabelOnly  = cell.status === 'absent' || cell.status === 'on_leave' || cell.status === 'half_leave' || isTodayNotIn;
                  const meta         = isTodayNotIn ? STATUS_META.absent : (STATUS_META[cell.status] || STATUS_META.scheduled);
                  const statusLabel  = isTodayNotIn ? '미출근'
                    : (cell.status === 'half_leave' && cell.half_leave_type) ? `반차(${cell.half_leave_type})` : meta.label;
                  const hasActual  = Boolean(cell.check_in);
                  const timeLabel  = isLabelOnly ? null
                    : hasActual ? `${cell.check_in}${cell.check_out ? `-${cell.check_out}` : ''}`
                    : (cell.schedule_start ? `${cell.schedule_start}${cell.schedule_end ? `-${cell.schedule_end}` : ''}` : null);
                  const locationLabel = (hasActual && !isLabelOnly) ? (cell.registered_ip_label || cell.check_in_address || null) : null;
                  // 타임라인 바
                  const barStartTime = isLabelOnly ? null : (hasActual ? cell.check_in : cell.schedule_start);
                  const barEndTime   = isLabelOnly ? null : (hasActual ? cell.check_out : cell.schedule_end);
                  const barStartPct  = timeToPercent(barStartTime);
                  const barEndPct    = timeToPercent(barEndTime);
                  const showBar      = barStartPct !== null && barEndPct !== null && barEndPct > barStartPct;

                  return (
                    <div
                      key={cell.user_id}
                      style={{
                        position: 'relative', fontSize: 11, padding: '1px 5px',
                        background: meta.bg, color: meta.text, border: `1px solid ${meta.border}`,
                        borderRadius: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        overflow: 'hidden', whiteSpace: 'nowrap',
                      }}
                    >
                      {showBar && (
                        <div style={{
                          position: 'absolute', top: 0, bottom: 0,
                          left: `${barStartPct}%`, width: `${barEndPct - barStartPct}%`,
                          background: meta.text, opacity: 0.12, borderRadius: 2, pointerEvents: 'none', zIndex: 0,
                        }} />
                      )}
                      <span style={{ position: 'relative', zIndex: 1, overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1, minWidth: 0 }}>
                        {cell.name}
                        {isLabelOnly
                          ? <span style={{ marginLeft: 3, fontWeight: 600 }}>{statusLabel}</span>
                          : timeLabel && <span style={{ marginLeft: 3, opacity: 0.7 }}>{timeLabel}</span>
                        }
                      </span>
                      {locationLabel && (
                        <span style={{ position: 'relative', zIndex: 1, marginLeft: 4, opacity: 0.65, flexShrink: 0, fontSize: 10 }}>{locationLabel}</span>
                      )}
                    </div>
                  );
                })}
                {chipCells.length > 4 && (
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'right' }}>+{chipCells.length - 4}명</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
